import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { Sparkles } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { Comment, Job, Task } from '@allemnii-pulse/shared';
import { db } from '../../lib/firebase.js';
import { api } from '../../lib/api.js';
import { useDocument } from '../../lib/firestore.js';
import { Button } from '../../components/ui/Button.js';
import { asDate, relativeTime } from '../../lib/time.js';
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/Dialog.js';
import { StatusBadge } from '../../components/ui/StatusBadge.js';
import { UserName } from '../../components/UserName.js';

const RECENT_TASKS = 8;
const RECENT_COMMENTS = 8;
/** Comments read per task — enough to surface the latest few project-wide without reading every thread. */
const COMMENTS_PER_TASK = 3;

interface TaskComment extends Comment {
  taskId: string;
}

const byNewest = (a: { createdAt: unknown }, b: { createdAt: unknown }) =>
  asDate(b.createdAt).getTime() - asDate(a.createdAt).getTime();

/** One-shot (not live) read of each task's newest comments, fetched only while the dialog is open. */
function useRecentComments(projectId: string, tasks: Task[], enabled: boolean) {
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loading, setLoading] = useState(false);
  const taskKey = tasks.map((t) => t.id).join(',');

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(
      tasks.map(async (t) => {
        const snap = await getDocs(
          query(
            collection(db, `projects/${projectId}/tasks/${t.id}/comments`),
            orderBy('createdAt', 'desc'),
            limit(COMMENTS_PER_TASK),
          ),
        );
        return snap.docs.map((d) => ({ ...(d.data() as Comment), taskId: t.id }));
      }),
    )
      .then((all) => {
        if (!cancelled) setComments(all.flat().sort(byNewest));
      })
      .catch(() => {
        if (!cancelled) setComments([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // taskKey stands in for `tasks`, which is a fresh array on every snapshot.
  }, [projectId, taskKey, enabled]);

  return { comments, loading };
}

/** Sanitized markdown. Long comments scroll inside `className`'s max-height rather than being cut
 * mid-syntax the way a character-count truncation would. */
function Markdown({ source, className }: { source: string; className?: string }) {
  const html = DOMPurify.sanitize(marked.parse(source, { async: false, breaks: true, gfm: true }));
  return (
    <div className={`doc-prose ${className ?? ''}`} dangerouslySetInnerHTML={{ __html: html }} />
  );
}

/** Generates a written progress report server-side (POST .../progress-summary) and shows it.
 * Not persisted: closing the dialog discards it, and each click runs a fresh generation. */
function AiSummary({ projectId, hasTasks }: { projectId: string; hasTasks: boolean }) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const { data: job } = useDocument<Job>(jobId ? `jobs/${jobId}` : null);

  useEffect(() => {
    if (job?.status === 'succeeded' && job.result) {
      setSummary((job.result as { summary: string }).summary);
      setJobId(null);
    } else if (job?.status === 'failed') {
      setError(job.error ?? 'Could not generate a summary. Please try again.');
      setJobId(null);
    }
  }, [job]);

  const working = starting || jobId !== null;

  async function generate() {
    setStarting(true);
    setError(null);
    try {
      const { jobId: id } = await api.post<{ jobId: string }>(
        `/projects/${projectId}/progress-summary`,
        {},
      );
      setJobId(id);
    } catch {
      setError('Could not start the summary. Please try again.');
    } finally {
      setStarting(false);
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted">AI summary</h3>
        <Button
          type="button"
          className="ml-auto"
          disabled={working || !hasTasks}
          onClick={() => void generate()}
        >
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          {working ? 'Summarising…' : summary ? 'Regenerate' : 'Generate summary'}
        </Button>
      </div>
      {error && <p className="text-sm text-danger">{error}</p>}
      {summary && (
        <Markdown
          source={summary}
          className="rounded-md border border-border bg-surface-2 p-3 text-sm"
        />
      )}
      {!summary && !working && !error && (
        <p className="text-xs text-muted">
          Reads the recent tasks, their comments and statuses and writes a short progress report.
        </p>
      )}
    </section>
  );
}

export function LatestProgressDialog({
  open,
  onOpenChange,
  projectId,
  tasks,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  tasks: Task[];
}) {
  const { comments, loading } = useRecentComments(projectId, tasks, open);
  const titleById = useMemo(() => new Map(tasks.map((t) => [t.id, t.title])), [tasks]);
  const latestCommentByTask = useMemo(() => {
    const map = new Map<string, TaskComment>();
    for (const c of comments) if (!map.has(c.taskId)) map.set(c.taskId, c);
    return map;
  }, [comments]);

  const recentTasks = useMemo(
    () =>
      [...tasks]
        .sort((a, b) => asDate(b.updatedAt).getTime() - asDate(a.updatedAt).getTime())
        .slice(0, RECENT_TASKS),
    [tasks],
  );

  const done = tasks.filter((t) => t.status === 'done').length;
  const inProgress = tasks.filter((t) => t.status === 'in_progress').length;
  const blocked = tasks.filter((t) => t.status === 'blocked');
  const inReview = tasks.filter((t) => t.status === 'testing' || t.status === 'in_review').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Latest progress</DialogTitle>
        <DialogDescription>
          {tasks.length === 0
            ? 'No tasks yet.'
            : `${done} of ${tasks.length} tasks done · ${inProgress} in progress · ${inReview} in review/testing · ${blocked.length} blocked`}
        </DialogDescription>
      </DialogHeader>

      <div className="mt-4 flex max-h-[60dvh] flex-col gap-5 overflow-y-auto pr-1">
        <AiSummary projectId={projectId} hasTasks={tasks.length > 0} />

        {blocked.length > 0 && (
          <section className="flex flex-col gap-2">
            <h3 className="text-[10px] font-semibold uppercase tracking-wide text-danger">
              Blocked
            </h3>
            {blocked.map((t) => (
              <Link
                key={t.id}
                to={`/projects/${projectId}/tasks/${t.id}`}
                className="break-words text-sm hover:text-accent"
              >
                {t.title}
              </Link>
            ))}
          </section>
        )}

        <section className="flex flex-col gap-2">
          <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted">
            Recently updated tasks
          </h3>
          {recentTasks.length === 0 ? (
            <p className="text-xs text-muted">Nothing yet.</p>
          ) : (
            <div className="flex flex-col divide-y divide-border">
              {recentTasks.map((t) => {
                const latest = latestCommentByTask.get(t.id);
                return (
                  <div key={t.id} className="flex flex-col gap-1 py-2">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/projects/${projectId}/tasks/${t.id}`}
                        className="min-w-0 flex-1 truncate text-sm font-medium hover:text-accent"
                      >
                        {t.title}
                      </Link>
                      <StatusBadge status={t.status} />
                      <span className="w-16 shrink-0 text-right text-xs text-muted">
                        {relativeTime(t.updatedAt)}
                      </span>
                    </div>
                    {latest && (
                      <div className="text-xs text-muted">
                        <UserName uid={latest.authorUid} showStatus={false} className="font-medium" />
                        <Markdown source={latest.body} className="max-h-16 overflow-hidden" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted">
            Latest comments
          </h3>
          {loading ? (
            <p className="text-xs text-muted">Loading…</p>
          ) : comments.length === 0 ? (
            <p className="text-xs text-muted">No comments yet.</p>
          ) : (
            <div className="flex flex-col divide-y divide-border">
              {comments.slice(0, RECENT_COMMENTS).map((c) => (
                <div key={`${c.taskId}/${c.id}`} className="flex flex-col gap-1 py-2">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted">
                    <UserName uid={c.authorUid} showStatus={false} className="font-medium text-text" />
                    <span>on</span>
                    <Link
                      to={`/projects/${projectId}/tasks/${c.taskId}`}
                      className="min-w-0 flex-1 truncate hover:text-accent"
                    >
                      {titleById.get(c.taskId) ?? 'Task'}
                    </Link>
                    {c.statusTag && (
                      <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px]">
                        {c.statusTag}
                      </span>
                    )}
                    <span>{relativeTime(c.createdAt)}</span>
                  </div>
                  <Markdown source={c.body} className="max-h-40 overflow-y-auto text-sm" />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Dialog>
  );
}
